import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, FormControl, InputLabel, MenuItem,
  Paper, Select, SelectChangeEvent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon, Print as PrintIcon } from '@mui/icons-material';
import { SalesReportService } from '../../services/salesReport.service';
import { CustomerService } from '../../services/customer.service';
import { CustomerStatement as CustomerStatementData } from '../../interfaces/sales/salesReport.interface';
import { Customer } from '../../interfaces/sales/customer.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { downloadCsv } from '../../utils/csv';

function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const CustomerStatement: React.FC = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(true);

  const [customerId, setCustomerId] = useState<string>(searchParams.get('customerId') ?? '');
  const [from, setFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return toYmd(d);
  });
  const [to, setTo] = useState<string>(() => toYmd(new Date()));

  const [statement, setStatement] = useState<CustomerStatementData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution();
    CustomerService.listByInstitution(institutionId, 0, 200)
      .then(page => setCustomers(page.content))
      .catch(e => toast.showApiError(e, 'Failed to load customers'))
      .finally(() => setCustomersLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRun = async () => {
    if (!customerId) return;
    setStatement(null);
    try {
      setLoading(true);
      const data = await SalesReportService.statement(customerId, from, to);
      setStatement(data);
    } catch (e) {
      toast.showApiError(e, 'Failed to load customer statement');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!statement) return;
    const headers = ['Date', 'Entry #', 'Type', 'Reference', 'Debit', 'Credit', 'Running Balance'];
    const dataRows = statement.rows.map(r => [
      r.date,
      r.entryNumber,
      r.documentType,
      r.reference,
      r.debit,
      r.credit,
      r.runningBalance,
    ]);
    const openingRow = ['', '', 'Opening Balance', '', '', '', statement.openingBalance];
    const closingRow = ['', '', 'Closing Balance', '', '', '', statement.closingBalance];
    downloadCsv('customer-statement.csv', headers, [openingRow, ...dataRows, closingRow]);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Typography variant="h5" gutterBottom>Customer Statement</Typography>

      {/* Filter row */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
        <FormControl sx={{ minWidth: 260 }} size="small">
          <InputLabel id="customer-select-label">Customer</InputLabel>
          <Select
            labelId="customer-select-label"
            label="Customer"
            value={customerId}
            onChange={(e: SelectChangeEvent) => {
              setCustomerId(e.target.value);
              setStatement(null);
            }}
            disabled={customersLoading}
          >
            {customers.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="From"
          type="date"
          size="small"
          value={from}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={to}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Button
          variant="contained"
          onClick={handleRun}
          disabled={!customerId || loading}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Run'}
        </Button>
      </Box>

      {/* Statement area */}
      {statement && (
        <Box>
          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </Box>

          {/* Printable area */}
          <Paper sx={{ p: 3 }} className="printable-area">
            {/* Statement header */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">{statement.customerName}</Typography>
              <Typography variant="body2" color="text.secondary">
                Period: {statement.fromDate} – {statement.toDate}
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
                <Typography variant="body2">
                  <strong>Opening Balance:</strong> {statement.openingBalance.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  <strong>Closing Balance:</strong> {statement.closingBalance.toFixed(2)}
                </Typography>
              </Box>
            </Box>

            {/* Statement table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Entry #</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell align="right">Debit</TableCell>
                    <TableCell align="right">Credit</TableCell>
                    <TableCell align="right">Running Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statement.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No transactions in this period.</TableCell>
                    </TableRow>
                  ) : (
                    statement.rows.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.entryNumber}</TableCell>
                        <TableCell>{row.documentType}</TableCell>
                        <TableCell>{row.reference}</TableCell>
                        <TableCell align="right">{row.debit > 0 ? row.debit.toFixed(2) : ''}</TableCell>
                        <TableCell align="right">{row.credit > 0 ? row.credit.toFixed(2) : ''}</TableCell>
                        <TableCell align="right">{row.runningBalance.toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default CustomerStatement;
